import { FunctionComponent, useEffect, useRef } from 'react'
import { useFlag } from '@afterpaytouch/feature'
import { Features } from '../../model/features'
import { isCashGrantHandoffPending } from './cashAuthJourney'
import { buildJourneyStartedEvent, buildRecordCompletedJourneyPayload } from './contract'
import { EsidContextProps, useEsid } from './EsidContext'
import { clearJourneyMarker, readJourneyMarker, writeJourneyMarker } from './journeyMarker'
import { datadogUserJourneySink, UserJourneySink } from './sink'
import { JourneyOutcome, JourneyTerminationReason, UserJourney } from './types'
import { useUserJourneyContext } from './UserJourneyContext'

export interface UserJourneyReportingProps {
  sink?: UserJourneySink
}

export const UserJourneyReporting: FunctionComponent<UserJourneyReportingProps> = ({ sink = datadogUserJourneySink }) => {
  const enabled = useFlag(Features.USER_JOURNEY_INSTRUMENTATION)
  const { active, subscribe } = useUserJourneyContext()
  const { esid }: EsidContextProps = useEsid()
  const reportedCompletions = useRef<Set<string>>(new Set())
  const reconciledMarker = useRef(false)
  const resumedEsids = useRef<Map<string, string>>(new Map())
  const monotonicStarts = useRef<Map<string, number>>(new Map())
  const activeRef = useRef(active)
  activeRef.current = active

  useEffect(() => {
    if (!enabled) {
      if (reconciledMarker.current) {
        clearJourneyMarker()
      }
      return undefined
    }

    const reportCompleted = (journey: UserJourney, outcome: JourneyOutcome, terminationReason?: JourneyTerminationReason): void => {
      if (reportedCompletions.current.has(journey.id)) {
        return
      }
      reportedCompletions.current.add(journey.id)
      clearJourneyMarker()
      sink.journeyCompleted(
        buildRecordCompletedJourneyPayload(
          journey,
          outcome,
          terminationReason,
          resumedEsids.current.get(journey.id) ?? esid,
          monotonicStarts.current.get(journey.id)
        )
      )
      monotonicStarts.current.delete(journey.id)
    }

    if (!reconciledMarker.current) {
      reconciledMarker.current = true
      try {
        const orphaned = readJourneyMarker()
        if (orphaned !== undefined && orphaned.handoff !== true && !reportedCompletions.current.has(orphaned.journey.id)) {
          reportedCompletions.current.add(orphaned.journey.id)
          clearJourneyMarker()
          sink.journeyCompleted(
            buildRecordCompletedJourneyPayload(orphaned.journey, JourneyOutcome.TERMINATED, JourneyTerminationReason.JOURNEY_RESTARTED, orphaned.esid)
          )
        }
      } catch {
        // reconciliation is best-effort; live reporting must still start
      }
    } else {
      const activeJourney = activeRef.current
      if (activeJourney !== undefined) {
        writeJourneyMarker(activeJourney, resumedEsids.current.get(activeJourney.id) ?? esid, isCashGrantHandoffPending())
      }
    }

    return subscribe(({ action, prevState, state }) => {
      switch (action.type) {
        case 'USER_JOURNEY_START': {
          const prior = prevState.journeys[action.name]
          if (prior !== undefined) {
            reportCompleted(prior, JourneyOutcome.TERMINATED, JourneyTerminationReason.JOURNEY_RESTARTED)
          }
          const started = state.journeys[action.name]
          if (started !== undefined) {
            monotonicStarts.current.set(started.id, performance.now())
            writeJourneyMarker(started, esid)
            sink.journeyStarted(buildJourneyStartedEvent(started, esid))
          }
          break
        }
        case 'USER_JOURNEY_RESUME': {
          const resumedJourney = state.journeys[action.name]
          if (resumedJourney !== undefined && prevState.journeys[action.name] === undefined) {
            resumedEsids.current.set(resumedJourney.id, action.esid)
            writeJourneyMarker(resumedJourney, action.esid)
          }
          break
        }
        case 'USER_JOURNEY_COMPLETE': {
          const journey = prevState.journeys[action.name]
          if (journey !== undefined) {
            reportCompleted(journey, action.outcome, action.terminationReason)
          }
          break
        }
        default: {
          const journey = state.journeys[action.name]
          if (journey !== undefined) {
            writeJourneyMarker(journey, resumedEsids.current.get(journey.id) ?? esid, isCashGrantHandoffPending())
          }
          break
        }
      }
    })
  }, [enabled, subscribe, esid, sink])

  return null
}
